<template>
  <div>
    <component :is="'script'" type="application/ld+json" v-html="breadcrumbJson" />
    <BaseContentPage
      :title="content.title"
      :heroTitle="content.heroTitle"
      :subtitle="content.subtitle"
      :sections="content.sections"
      :highlights="content.highlights"
      :contentType="content.type"
      :links="content.links"
      :hubCards="content.guideHubCards || []"
      :navPage="content.navPage || 'download'"
      :showDownloadButton="content.navPage !== 'privacy'"
    />
  </div>
</template>

<script>
import BaseContentPage from '@/components/BaseContentPage.vue';
import { getRelatedLinksForPath, pageContentMap, pageBreadcrumbMap } from '@/data/pageContent';

const SITE_BASE = 'https://gbwhetsapp.com';

function buildBreadcrumbLdJson(path, currentName) {
  const crumbs = [
    { position: 1, name: 'Home', item: `${SITE_BASE}/` },
  ];
  const mapEntry = pageBreadcrumbMap[path];
  if (mapEntry) {
    if (mapEntry.parentName) {
      crumbs.push({ position: 2, name: mapEntry.parentName, item: `${SITE_BASE}${path}` });
    }
    crumbs.push({ position: crumbs.length + 1, name: currentName, item: `${SITE_BASE}${path}` });
  } else {
    crumbs.push({ position: 2, name: currentName, item: `${SITE_BASE}${path}` });
  }
  crumbs.forEach((c, i) => { c.position = i + 1; });
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map(({ position, name, item }) => ({
      '@type': 'ListItem',
      position,
      name,
      item,
    })),
  };
}

export default {
  name: 'ContentPage',
  components: {
    BaseContentPage,
  },
  computed: {
    enrichedBlocks() {
      const path = this.$route.path || '';
      if (path.includes('/safety/')) {
        return {
          type: 'Safety Guide',
          highlights: [
            'No unofficial build can guarantee permanent anti-ban protection.',
            'Always prioritize verified APK sources and local backups.',
            'Use official WhatsApp for finance, enterprise, and compliance-critical usage.',
          ],
          extraSections: [
            {
              heading: 'Practical Risk-Control Checklist',
              body: '<ul><li>Keep a secondary communication channel in case account restrictions appear.</li><li>Avoid automation-heavy behavior and bulk messaging patterns.</li><li>Review app permissions regularly and keep only what is required.</li></ul>',
            },
          ],
        };
      }
      if (path.includes('/features/')) {
        return {
          type: 'Feature Guide',
          highlights: [
            'Feature behavior can vary by build and Android brand.',
            'Test privacy options in low-risk chats before global activation.',
            'Backup first before major updates or settings changes.',
          ],
          extraSections: [
            {
              heading: 'Usage Tips for Better Stability',
              body: '<p>After enabling new options, restart the app and observe notifications, media sending speed, and contact visibility for one day. Keep only the controls you truly need to reduce conflicts.</p>',
            },
          ],
        };
      }
      if (path.includes('/compare/')) {
        return {
          type: 'Comparison Guide',
          highlights: [
            'Compare by workflow fit, not by feature count alone.',
            'Stability and update transparency often matter more than novelty.',
            'Policy and security risk should always be part of your decision.',
          ],
          extraSections: [
            {
              heading: 'How to Choose Between Options',
              body: '<ol><li>List your must-have features.</li><li>Define your acceptable risk level.</li><li>Test one stable build at a time and evaluate reliability before switching again.</li></ol>',
            },
          ],
        };
      }
      if (path.includes('/guide/')) {
        return {
          type: 'Setup Guide',
          highlights: [
            'Create backups before install, update, or migration.',
            'Use stable network during verification and restore.',
            'Validate media, contacts, and chat history after setup.',
          ],
          extraSections: [
            {
              heading: 'After-Setup Optimization',
              body: '<ul><li>Adjust privacy defaults before daily use.</li><li>Disable restrictive battery optimization if message delivery is delayed.</li><li>Set a recurring backup reminder.</li></ul>',
            },
          ],
        };
      }
      if (path === '/guides/') {
        return {
          type: 'Guides Hub',
          highlights: [
            'Pick one guide at a time—install, update, or backup—before mixing steps.',
            'Download only from the verified download page and check permissions.',
            'If something fails, use the linked error guides instead of repeated retries.',
          ],
          extraSections: [],
        };
      }
      return {
        type: 'Knowledge Base',
        highlights: [
          'Use stable versions from trusted pages.',
          'Keep local backups before major changes.',
          'Match tools with your risk tolerance and account importance.',
        ],
        extraSections: [],
      };
    },
    content() {
      const sourceContent =
        pageContentMap[this.$route.path] || {
          title: 'Page Not Found',
          subtitle: 'The requested page content is currently unavailable.',
          sections: [
            {
              heading: 'Try These Pages',
              body: '<p>Please use the main navigation or visit the homepage and download section.</p>',
            },
          ],
        };
      const path = this.$route.path || '/';
      const resolvedLinks = Array.isArray(sourceContent.links)
        ? sourceContent.links
        : getRelatedLinksForPath(path);
      return {
        ...sourceContent,
        type: this.enrichedBlocks.type,
        highlights: this.enrichedBlocks.highlights,
        sections: [
          ...(sourceContent.sections || []),
          ...(sourceContent.skipEnrichedExtraSections ? [] : (this.enrichedBlocks.extraSections || [])),
        ],
        links: resolvedLinks,
      };
    },
    breadcrumbJson() {
      const path = this.$route.path || '/';
      const title = this.content.title || 'GB WhatsApp';
      return JSON.stringify(buildBreadcrumbLdJson(path, title));
    },
  },
};
</script>
